import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Heart, MessageCircle, UserPlus, Mail, X, Clock, Trash2, UserCheck, AlertCircle, RefreshCw, CheckCheck, ShieldAlert, FileText, ClipboardCheck, Award, Star, AtSign, Users, HeartHandshake, Sparkles, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PostModal from './PostModal.jsx';

import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { toast } from '../utils/toast.js';
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .notif-overlay { position: absolute; top: calc(100% + 10px); right: 0; z-index: 1005; pointer-events: none; }
  .notif-panel { pointer-events: all; width: min(400px, calc(100vw - 24px)); max-height: min(70vh, calc(100vh - 110px)); background: #f5f7f0; border: 1px solid rgba(50,97,77,0.28); border-radius: 20px; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 0 0 1px rgba(50,97,77,0.05), 0 24px 50px rgba(0,0,0,0.14), 0 0 60px rgba(50,97,77,0.07); animation: notifSlideIn 0.28s cubic-bezier(0.34,1.56,0.64,1); font-family: 'Sora', sans-serif; }
  @keyframes notifSlideIn { from { opacity: 0; transform: translateY(-14px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
  .notif-header { display: flex; align-items: center; justify-content: space-between; padding: 18px 20px 14px; border-bottom: 1px solid rgba(50,97,77,0.15); background: linear-gradient(180deg, rgba(50,97,77,0.1) 0%, rgba(245,247,240,0.5) 100%); flex-shrink: 0; }
  .notif-header-left { display: flex; align-items: center; gap: 10px; }
  .notif-header-icon { width: 34px; height: 34px; border-radius: 10px; background: linear-gradient(135deg, #32614d, #254a3a); display: flex; align-items: center; justify-content: center; box-shadow: 0 0 14px rgba(50,97,77,0.35); flex-shrink: 0; }
  .notif-title { font-size: 15px; font-weight: 700; color: #32614d; letter-spacing: -0.3px; }
  .notif-badge { background: linear-gradient(135deg, #faa002, #e08e00); color: #fff; font-size: 11px; font-weight: 800; font-family: 'JetBrains Mono', monospace; padding: 2px 8px; border-radius: 20px; min-width: 22px; text-align: center; box-shadow: 0 0 12px rgba(250,160,2,0.45); }
  .notif-header-actions { display: flex; align-items: center; gap: 8px; }
  .notif-mark-all-btn { display: flex; align-items: center; gap: 5px; padding: 6px 12px; background: rgba(50,97,77,0.1); border: 1px solid rgba(50,97,77,0.35); border-radius: 9px; color: #32614d; font-size: 12px; font-weight: 600; font-family: 'Sora', sans-serif; cursor: pointer; transition: all 0.2s; white-space: nowrap; }
  .notif-mark-all-btn:hover:not(:disabled) { background: rgba(50,97,77,0.18); box-shadow: 0 0 10px rgba(50,97,77,0.15); }
  .notif-mark-all-btn:disabled { opacity: 0.45; cursor: not-allowed; }
  .notif-close-btn { display: flex; align-items: center; justify-content: center; width: 30px; height: 30px; border: none; border-radius: 50%; background: transparent; color: #5c6b63; cursor: pointer; transition: all 0.2s; flex-shrink: 0; }
  .notif-close-btn:hover { background: rgba(50,97,77,0.12); color: #254a3a; }
  .notif-list { flex: 1; overflow-y: auto; padding: 8px; background: #f5f7f0; scrollbar-width: thin; scrollbar-color: rgba(50,97,77,0.25) transparent; }
  .notif-list::-webkit-scrollbar { width: 4px; }
  .notif-list::-webkit-scrollbar-thumb { background: rgba(50,97,77,0.25); border-radius: 4px; }
  .notif-item { display: flex; align-items: flex-start; gap: 11px; padding: 12px 11px; border-radius: 13px; cursor: pointer; transition: background 0.18s, border-color 0.18s; position: relative; margin-bottom: 3px; border: 1px solid transparent; }
  .notif-item.unread { background: rgba(50,97,77,0.08); border-color: rgba(50,97,77,0.18); }
  .notif-item.read { background: rgba(255,255,255,0.55); border-color: rgba(50,97,77,0.07); }
  .notif-item:hover { background: rgba(50,97,77,0.12); border-color: rgba(50,97,77,0.28); }
  .notif-item.is-follow-req { flex-direction: column; cursor: default; }
  .notif-item.is-post-deleted { cursor: default; }
  .notif-item.is-post-deleted:hover { background: rgba(239,68,68,0.06); border-color: rgba(239,68,68,0.18); }
  .notif-dot { position: absolute; top: 13px; right: 11px; width: 7px; height: 7px; background: #faa002; border-radius: 50%; box-shadow: 0 0 8px rgba(250,160,2,0.7); }
  .notif-avatar-wrap { position: relative; flex-shrink: 0; cursor: pointer; }
  .notif-avatar-wrap:hover .notif-avatar-img { border-color: rgba(50,97,77,0.7); transform: scale(1.04); }
  .notif-avatar-img { width: 42px; height: 42px; border-radius: 11px; object-fit: cover; border: 2px solid rgba(50,97,77,0.25); display: block; transition: border-color 0.2s, transform 0.2s; }
  .notif-type-badge { position: absolute; bottom: -4px; right: -4px; width: 19px; height: 19px; border-radius: 6px; display: flex; align-items: center; justify-content: center; border: 2px solid #f5f7f0; pointer-events: none; }
  .ntb-heart   { background: linear-gradient(135deg,#f43f5e,#e11d48); }
  .ntb-comment { background: linear-gradient(135deg,#faa002,#e08e00); }
  .ntb-follow  { background: linear-gradient(135deg,#32614d,#254a3a); }
  .ntb-message { background: linear-gradient(135deg,#faa002,#e08e00); }
  .ntb-system  { background: linear-gradient(135deg,#32614d,#254a3a); }
  .ntb-deleted { background: linear-gradient(135deg,#ef4444,#b91c1c); }
  .ntb-buddy   { background: linear-gradient(135deg,#9bc060,#6b9142); }
  .ntb-level   { background: linear-gradient(135deg,#f3b53a,#c9860f); }
  .notif-body { flex: 1; min-width: 0; padding-right: 14px; }
  .notif-sender-name { font-size: 13px; font-weight: 700; color: #32614d; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .notif-sender-name.admin-label { color: #dc2626; }
  .notif-action-text { font-size: 12.5px; color: #5a6e65; line-height: 1.45; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; word-break: break-word; white-space: pre-line; }
  .notif-link-chip { display: inline-flex; align-items: center; gap: 5px; margin-top: 5px; padding: 4px 9px; background: rgba(50,97,77,0.08); border: 1px solid rgba(50,97,77,0.22); border-radius: 20px; font-size: 11px; font-weight: 600; color: #32614d; text-decoration: none; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .notif-link-chip:hover { background: rgba(50,97,77,0.16); }
  .notif-deleted-reason { margin-top: 6px; padding: 6px 10px; background: rgba(239,68,68,0.07); border-left: 2px solid rgba(239,68,68,0.4); border-radius: 0 6px 6px 0; font-size: 11.5px; color: #b91c1c; font-style: italic; line-height: 1.4; }
  .notif-post-preview { margin-top: 5px; padding: 5px 9px; background: rgba(50,97,77,0.06); border-left: 2px solid rgba(50,97,77,0.3); border-radius: 0 5px 5px 0; font-size: 11.5px; color: #7a8a82; font-style: italic; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .notif-comment-preview { margin-top: 5px; padding: 5px 9px; background: rgba(250,160,2,0.07); border-left: 2px solid rgba(250,160,2,0.5); border-radius: 0 5px 5px 0; font-size: 11.5px; color: #7a8a82; font-style: italic; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .notif-time { display: flex; align-items: center; gap: 4px; margin-top: 5px; font-size: 11px; color: #9aaa9f; font-family: 'JetBrains Mono', monospace; }
  .notif-del-btn { flex-shrink: 0; width: 26px; height: 26px; border-radius: 7px; background: transparent; border: 1px solid transparent; color: #aabab2; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.18s; opacity: 0; margin-top: 2px; }
  .notif-item:hover .notif-del-btn { opacity: 1; }
  .notif-del-btn:hover { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.2); color: #ef4444; }
  .notif-follow-row { display: flex; align-items: flex-start; gap: 11px; width: 100%; }
  .notif-follow-btns { display: flex; gap: 7px; margin-top: 9px; padding-left: 53px; }
  .btn-accept { display: flex; align-items: center; gap: 5px; padding: 7px 14px; background: linear-gradient(135deg, #32614d, #254a3a); border: none; border-radius: 9px; color: #fff; font-size: 12px; font-weight: 700; font-family: 'Sora', sans-serif; cursor: pointer; transition: all 0.2s; box-shadow: 0 0 10px rgba(50,97,77,0.3); }
  .btn-accept:hover:not(:disabled) { opacity: 0.85; transform: translateY(-1px); box-shadow: 0 0 18px rgba(50,97,77,0.4); }
  .btn-accept:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }
  .btn-decline { display: flex; align-items: center; gap: 5px; padding: 7px 14px; background: rgba(255,255,255,0.7); border: 1px solid rgba(50,97,77,0.2); border-radius: 9px; color: #7a8a82; font-size: 12px; font-weight: 600; font-family: 'Sora', sans-serif; cursor: pointer; transition: all 0.2s; }
  .btn-decline:hover:not(:disabled) { background: rgba(239,68,68,0.07); border-color: rgba(239,68,68,0.22); color: #ef4444; }
  .btn-decline:disabled { opacity: 0.45; cursor: not-allowed; }
  .notif-loading, .notif-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px 24px; gap: 12px; text-align: center; }
  .notif-spinner { width: 30px; height: 30px; border: 2px solid rgba(50,97,77,0.15); border-top-color: #32614d; border-radius: 50%; animation: notifSpin 0.75s linear infinite; }
  @keyframes notifSpin { to { transform: rotate(360deg); } }
  .notif-empty-icon { width: 50px; height: 50px; border-radius: 14px; background: rgba(50,97,77,0.08); display: flex; align-items: center; justify-content: center; color: #32614d; }
  .notif-empty h3 { font-size: 14px; font-weight: 700; color: #32614d; margin: 0; }
  .notif-empty p  { font-size: 12px; color: #7a8a82; margin: 0; }
  .notif-footer { display: flex; align-items: center; justify-content: space-between; padding: 11px 18px; border-top: 1px solid rgba(50,97,77,0.15); background: rgba(245,247,240,0.95); flex-shrink: 0; }
  .notif-stats { display: flex; align-items: center; gap: 10px; font-size: 11.5px; color: #7a8a82; font-family: 'JetBrains Mono', monospace; }
  .notif-unread-stat { display: flex; align-items: center; gap: 4px; color: #faa002; }
  .notif-refresh-btn { display: flex; align-items: center; gap: 5px; padding: 6px 12px; background: transparent; border: 1px solid rgba(50,97,77,0.2); border-radius: 8px; color: #7a8a82; font-size: 12px; font-weight: 500; font-family: 'Sora', sans-serif; cursor: pointer; transition: all 0.2s; }
  .notif-refresh-btn:hover:not(:disabled) { border-color: rgba(50,97,77,0.4); color: #32614d; }
  .notif-refresh-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .notif-spinning { animation: notifSpin 1s linear infinite; }
  .notif-admin-icon { width: 42px; height: 42px; border-radius: 11px; background: linear-gradient(135deg, #ef4444, #b91c1c); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  @media (max-width: 768px) {
    .notif-overlay {
      position: fixed;
      top: 58px;
      left: 10px;
      right: 10px;
      z-index: 1200;
    }
    .notif-panel {
      width: 100%;
      max-width: none;
      max-height: min(68vh, calc(100vh - 86px));
      border-radius: 16px;
      box-shadow: 0 12px 30px rgba(0,0,0,0.16), 0 0 30px rgba(50,97,77,0.08);
    }
    .notif-header {
      padding: 14px 14px 12px;
      gap: 10px;
      align-items: flex-start;
    }
    .notif-header-left {
      gap: 8px;
      min-width: 0;
    }
    .notif-header-actions {
      flex-shrink: 0;
    }
    .notif-header-icon {
      width: 30px;
      height: 30px;
      border-radius: 9px;
    }
    .notif-title {
      font-size: 14px;
    }
    .notif-mark-all-btn {
      padding: 6px 10px;
      font-size: 11px;
    }
    .notif-list {
      padding: 6px;
    }
    .notif-item {
      gap: 9px;
      padding: 10px 9px;
      border-radius: 11px;
    }
    .notif-follow-row {
      gap: 9px;
    }
    .notif-avatar-img,
    .notif-admin-icon {
      width: 38px;
      height: 38px;
      border-radius: 10px;
    }
    .notif-body {
      padding-right: 8px;
    }
    .notif-sender-name {
      font-size: 12px;
    }
    .notif-action-text,
    .notif-post-preview,
    .notif-comment-preview,
    .notif-deleted-reason {
      font-size: 11px;
    }
    .notif-follow-btns {
      padding-left: 47px;
      gap: 6px;
      flex-wrap: wrap;
    }
    .btn-accept,
    .btn-decline {
      padding: 6px 11px;
      font-size: 11px;
    }
    .notif-footer {
      padding: 10px 12px;
      gap: 8px;
    }
    .notif-stats {
      font-size: 10.5px;
      gap: 8px;
      flex-wrap: wrap;
    }
    .notif-refresh-btn {
      padding: 6px 10px;
      font-size: 11px;
    }
  }
`;

const BASE_URL       = `${GREENWICH_BACKEND_URL}`;
const API            = `${BASE_URL}/api`;
const DEFAULT_AVATAR = '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';

const buildAvatarUrl = (src) => {
  if (!src || src.trim() === '') return DEFAULT_AVATAR;
  if (src.startsWith('http'))    return src;
  return `${BASE_URL}${src.startsWith('/') ? src : '/' + src}`;
};

const Avatar = ({ src, name, onClick }) => {
  const { t } = useTranslation();
  const [imgSrc, setImgSrc] = useState(() => buildAvatarUrl(src));
  useEffect(() => { setImgSrc(buildAvatarUrl(src)); }, [src]);
  return (
    <img
      src={imgSrc}
      alt={name || t('notifications.avatarAlt')}
      className="notif-avatar-img"
      title={t('notifications.viewProfile', { name: name || t('notifications.someone').toLowerCase() })}
      onClick={onClick}
      onError={() => setImgSrc(DEFAULT_AVATAR)}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Wrapper component — keeps PostModal alive even after NotificationModal closes
// ─────────────────────────────────────────────────────────────────────────────
const NotificationModal = (props) => {
  const [postModalId, setPostModalId] = useState(null);

  return (
    <>
      <NotificationModalCore
        {...props}
        onOpenPost={(id) => setPostModalId(id)}
      />
      <PostModal
        postId={postModalId}
        isOpen={!!postModalId}
        onClose={() => setPostModalId(null)}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Core NotificationModal
// ─────────────────────────────────────────────────────────────────────────────
// Bare-URL detector — used to pull a pasted link (e.g. a Facebook post URL)
// out of a raw notification body so it renders as a proper chip instead of
// sitting inline as unstyled plain text.
const URL_RE = /(https?:\/\/[^\s]+)/i;

function splitTextAndLink(raw) {
  if (!raw) return { text: '', link: null };
  const match = raw.match(URL_RE);
  if (!match) return { text: raw, link: null };
  const link = match[0].replace(/[),.;]+$/, ''); // trim trailing punctuation often pasted after a URL
  const text = (raw.slice(0, match.index) + raw.slice(match.index + match[0].length))
    .replace(/\s{2,}/g, ' ')
    .trim();
  return { text, link };
}

function linkLabel(link, fallback) {
  try { return new URL(link).hostname.replace(/^www\./, ''); }
  catch { return fallback; }
}

function truncateText(str, max = 140) {
  if (!str) return str;
  return str.length > max ? str.slice(0, max).trimEnd() + '…' : str;
}

const NotificationModalCore = ({ isOpen, onClose, onOpenPost }) => {
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [markingAll,    setMarkingAll]    = useState(false);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [processing,    setProcessing]    = useState({});
  const modalRef = useRef(null);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('token');

  /* ── Outside click → close NotifModal ── */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(e.target) &&
        !e.target.closest('.notification-btn')
      ) {
        onClose();
      }
    };
    const tid = setTimeout(() => document.addEventListener('mousedown', handler), 150);
    return () => { clearTimeout(tid); document.removeEventListener('mousedown', handler); };
  }, [isOpen, onClose]);

  /* ── Load on open ── */
  useEffect(() => { if (isOpen) loadNotifications(); }, [isOpen]);

  const loadNotifications = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      const arr = Array.isArray(data) ? data
        : data.notifications ? data.notifications
        : data.data          ? data.data
        : [];
      const sorted = [...arr].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      setNotifications(sorted);
      setUnreadCount(sorted.filter(n => !n.isRead).length);
    } catch (err) { console.error('loadNotifications:', err); }
    finally { setLoading(false); }
  }, []);

  const markAsRead = useCallback(async (id) => {
    const token = getToken();
    if (!token || !id) return;
    setNotifications(prev => prev.map(n => (n._id || n.id) === id ? { ...n, isRead: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await fetch(`${API}/notifications/${id}/read`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) { console.error('markAsRead:', err); }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const token = getToken();
    if (!token || markingAll) return;
    const unread = notifications.filter(n => !n.isRead);
    if (!unread.length) return;
    setMarkingAll(true);
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try {
      const res = await fetch(`${API}/notifications/read-all`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        await Promise.all(unread.map(n =>
          fetch(`${API}/notifications/${n._id || n.id}/read`, {
            method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
          })
        ));
      }
    } catch { /* already updated locally */ }
    finally { setMarkingAll(false); }
  }, [notifications, markingAll]);

  const deleteNotification = useCallback(async (id, e) => {
    e?.stopPropagation();
    const token = getToken();
    if (!token || !id) return;
    const notif = notifications.find(n => (n._id || n.id) === id);
    setNotifications(prev => prev.filter(n => (n._id || n.id) !== id));
    if (notif && !notif.isRead) setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await fetch(`${API}/notifications/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) { console.error('deleteNotification:', err); }
  }, [notifications]);

  /* ── Avatar click → profile ── */
  const handleAvatarClick = useCallback((e, notification) => {
    e.stopPropagation();
    // For post_deleted notifications, don't navigate (it's admin)
    if (notification.type === 'post_deleted') return;
    const senderId = notification.sender?._id || notification.sender?.id;
    if (!senderId) return;
    const id = notification._id || notification.id;
    if (id && !notification.isRead) markAsRead(id);
    navigate(`/profile/${senderId}`);
    onClose();
  }, [markAsRead, navigate, onClose]);

  /* ── Item click ── */
  const handleItemClick = useCallback((notification) => {
    const id = notification._id || notification.id;
    if (id && !notification.isRead) markAsRead(id);
    const { type } = notification;

    // post_deleted — just mark as read, no navigation
    if (type === 'post_deleted') return;

    if (type === 'admin_announcement') {
      const link = notification.metadata?.link;
      const programId = notification.metadata?.programId;
      if (link && link.startsWith('/')) { navigate(link); onClose(); return; }
      if (link) { window.location.assign(link); return; }
      if (programId) { navigate(`/programs?program=${programId}`); onClose(); return; }
      return;
    }

    if (type === 'application_submitted') {
      navigate('/programs/my-submissions'); onClose(); return;
    }
    if (['application_response', 'application_attended', 'certificate_issued'].includes(type)) {
      navigate('/dashboard', { state: { section: type === 'certificate_issued' ? 'certificates' : 'applications' } });
      onClose(); return;
    }
    if (type === 'org_review_received') {
      navigate('/organization-account-profile'); onClose(); return;
    }
    if (['join_request', 'join_request_response'].includes(type)) {
      const groupId = notification.metadata?.groupId || notification.group;
      navigate(groupId ? `/community/group/${groupId}` : '/my-communities'); onClose(); return;
    }

    if (['like', 'comment', 'post_like', 'post_comment', 'mention'].includes(type)) {
      const postId = (notification.post && typeof notification.post === 'object')
        ? (notification.post._id || notification.post.id)
        : notification.post
        || notification.postId
        || notification.metadata?.postId;

      if (postId) {
        onClose();
        setTimeout(() => onOpenPost?.(postId), 0);
        return;
      }
    }

    if (['follow_accepted', 'follow'].includes(type)) {
      const sid = notification.sender?._id || notification.sender?.id;
      if (sid) { navigate(`/profile/${sid}`); onClose(); return; }
    }
    if (['message', 'new_message'].includes(type)) {
      navigate('/messages'); onClose(); return;
    }
    if (type === 'buddy_mentor_application') {
      navigate('/admin/buddy-mentors'); onClose(); return;
    }
    if (type?.startsWith('buddy_')) {
      navigate('/buddy'); onClose(); return;
    }
    if (type === 'level_up') {
      navigate('/levels'); onClose(); return;
    }
    if (type === 'project_match') {
      navigate('/programs'); onClose(); return;
    }
    if (type === 'greencoin_reward') {
      navigate('/greencoins'); onClose(); return;
    }
    if (type === 'org_balance_low') {
      navigate('/organization/greencoins'); onClose(); return;
    }
    if (type === 'support_contact_request') {
      navigate('/admin/inbox'); onClose(); return;
    }
    if (type === 'pro_interest' || type === 'greencoin_interest') {
      navigate('/admin/users'); onClose(); return;
    }
    if (type === 'refund_requested') {
      navigate('/admin/refunds'); onClose(); return;
    }
    if (type === 'refund_decided') {
      navigate('/settings'); onClose(); return;
    }
    const sid = notification.sender?._id || notification.sender?.id;
    if (sid) { navigate(`/profile/${sid}`); onClose(); }
  }, [markAsRead, navigate, onClose, onOpenPost]);

  /* ── Follow accept/reject — 3 fallback strategies ── */
  const handleFollowAction = useCallback(async (notification, action, e) => {
    e?.stopPropagation();
    const token = getToken();
    if (!token) return;
    const requestId =
      (notification.followRequest && typeof notification.followRequest === 'object'
        ? notification.followRequest._id : notification.followRequest) ||
      notification.metadata?.requestId;
    const senderId = notification.sender?._id || notification.sender?.id;
    const notifId  = notification._id || notification.id;
    const procKey  = requestId || senderId || notifId;
    if (!requestId && !senderId) { toast(t('notifications.missingFollowData')); return; }

    setProcessing(prev => ({ ...prev, [procKey]: true }));
    let success = false;
    try {
      if (requestId) {
        const r = await fetch(`${API}/follow/${action}/${requestId}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }).catch(() => null);
        if (r?.ok) success = true;
      }
      if (!success && requestId) {
        const r = await fetch(`${API}/follow/requests/${requestId}/${action}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }).catch(() => null);
        if (r?.ok) success = true;
      }
      if (!success && senderId) {
        const ep = action === 'accept'
          ? `${API}/users/${senderId}/accept-follow`
          : `${API}/users/${senderId}/reject-follow`;
        const r = await fetch(ep, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }).catch(() => null);
        if (r?.ok) success = true;
      }
      if (success) {
        setNotifications(prev => prev.filter(n => (n._id || n.id) !== notifId));
        if (!notification.isRead) setUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        toast(t('notifications.followActionFailed', { action: action === 'accept' ? t('notifications.accept').toLowerCase() : t('notifications.decline').toLowerCase() }));
      }
    } catch (err) {
      console.error(`Follow ${action}:`, err);
      toast(t('notifications.networkError'));
    } finally {
      setProcessing(prev => ({ ...prev, [procKey]: false }));
    }
  }, []);

  const getTypeClass  = (type) => ({
    like:           'ntb-heart',
    post_like:      'ntb-heart',
    comment:        'ntb-comment',
    post_comment:   'ntb-comment',
    follow:         'ntb-follow',
    follow_request: 'ntb-follow',
    follow_accepted:'ntb-follow',
    message:        'ntb-message',
    new_message:    'ntb-message',
    post_deleted:   'ntb-deleted',
    mention:        'ntb-comment',
    application_submitted:  'ntb-follow',
    application_response:   'ntb-follow',
    application_attended:   'ntb-follow',
    certificate_issued:     'ntb-follow',
    org_review_received:    'ntb-follow',
    join_request:            'ntb-follow',
    join_request_response:   'ntb-follow',
    buddy_mentor_application: 'ntb-buddy',
    buddy_mentor_approved:    'ntb-buddy',
    buddy_mentor_rejected:    'ntb-buddy',
    buddy_request:            'ntb-buddy',
    buddy_request_accepted:   'ntb-buddy',
    buddy_request_declined:   'ntb-buddy',
    buddy_mentorship_ended:   'ntb-buddy',
    buddy_certificate_earned: 'ntb-buddy',
    level_up:                 'ntb-level',
  }[type] || 'ntb-system');

  const getTypeIcon   = (type) => ({
    like:           <Heart size={10} color="#fff"/>,
    post_like:      <Heart size={10} color="#fff"/>,
    comment:        <MessageCircle size={10} color="#fff"/>,
    post_comment:   <MessageCircle size={10} color="#fff"/>,
    follow:         <UserPlus size={10} color="#fff"/>,
    follow_request: <UserPlus size={10} color="#fff"/>,
    follow_accepted:<UserCheck size={10} color="#fff"/>,
    message:        <Mail size={10} color="#fff"/>,
    new_message:    <Mail size={10} color="#fff"/>,
    post_deleted:   <Trash2 size={10} color="#fff"/>,
    mention:        <AtSign size={10} color="#fff"/>,
    application_submitted: <FileText size={10} color="#fff"/>,
    application_response:  <FileText size={10} color="#fff"/>,
    application_attended:  <ClipboardCheck size={10} color="#fff"/>,
    certificate_issued:    <Award size={10} color="#fff"/>,
    org_review_received:   <Star size={10} color="#fff"/>,
    join_request:           <Users size={10} color="#fff"/>,
    join_request_response:  <Users size={10} color="#fff"/>,
    buddy_mentor_application: <HeartHandshake size={10} color="#fff"/>,
    buddy_mentor_approved:    <HeartHandshake size={10} color="#fff"/>,
    buddy_mentor_rejected:    <HeartHandshake size={10} color="#fff"/>,
    buddy_request:            <HeartHandshake size={10} color="#fff"/>,
    buddy_request_accepted:   <HeartHandshake size={10} color="#fff"/>,
    buddy_request_declined:   <HeartHandshake size={10} color="#fff"/>,
    buddy_mentorship_ended:   <HeartHandshake size={10} color="#fff"/>,
    buddy_certificate_earned: <Award size={10} color="#fff"/>,
    level_up:                 <Sparkles size={10} color="#fff"/>,
  }[type] || <Bell size={10} color="#fff"/>);

  const getActionText = (type) => ({
    like:           t('notifications.liked'),
    post_like:      t('notifications.liked'),
    comment:        t('notifications.commented'),
    post_comment:   t('notifications.commented'),
    follow:         t('notifications.followed'),
    follow_request: t('notifications.followRequest'),
    follow_accepted:t('notifications.followAccepted'),
    message:        t('notifications.sentMessage'),
    new_message:    t('notifications.sentMessage'),
    post_deleted:   t('notifications.postRemoved'),
    mention:                 'mentioned you',
    application_submitted:   'submitted an application',
    application_response:    'responded to your application',
    application_attended:    'confirmed your attendance',
    certificate_issued:      'issued you a certificate',
    org_review_received:     'left you a review',
    join_request:            'requested to join',
    join_request_response:   'responded to your join request',
    level_up:                 'leveled up',
  }[type] || t('notifications.genericNotification'));

  const formatTime = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts);
    const m = Math.floor(diff/60000), h = Math.floor(diff/3600000), d = Math.floor(diff/86400000);
    if (m < 1) return t('notifications.justNow');
    if (m < 60) return `${m}m`;
    if (h < 24) return `${h}h`;
    if (d < 7)  return `${d}d`;
    // Follows the same Intl.DateTimeFormat(i18n.language, ...) convention
    // used elsewhere in the app (see utils/deadline.js) instead of a
    // hardcoded 'en-US' that ignored whatever language the member is using.
    try {
      return new Intl.DateTimeFormat(i18n.language || undefined, { month: 'short', day: 'numeric' }).format(new Date(ts));
    } catch {
      return new Date(ts).toLocaleDateString();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{styles}</style>
      <div className="notif-overlay clay-modal-backdrop">
        <div
          className="notif-panel clay-modal-enter"
          ref={modalRef}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >

          <div className="notif-header">
            <div className="notif-header-left">
              <div className="notif-header-icon"><Bell size={15} color="#fff" /></div>
              <span className="notif-title">{t('notifications.title')}</span>
              {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
            </div>
            <div className="notif-header-actions">
              {unreadCount > 0 && (
                <button className="notif-mark-all-btn clay-press clay-focus-glow" onClick={markAllAsRead} disabled={markingAll || loading}>
                  <CheckCheck size={13} />{markingAll ? t('notifications.marking') : t('notifications.markAllRead')}
                </button>
              )}
              <button className="notif-close-btn" onClick={onClose} aria-label={t('notifications.close', 'Close')}>
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="notif-list">
            {loading && !notifications.length ? (
              <div className="notif-loading">
                <div className="notif-spinner clay-spinner" />
                <span style={{ fontSize: 12, color: '#7a8a82' }}>{t('notifications.loading')}</span>
              </div>
            ) : !notifications.length ? (
              <div className="notif-empty">
                <div className="notif-empty-icon"><Bell size={21} /></div>
                <h3>{t('notifications.allCaughtUp')}</h3>
                <p>{t('notifications.noNew')}</p>
              </div>
            ) : notifications.map((notif) => {
              const id            = notif._id || notif.id;
              const isFollowReq   = notif.type === 'follow_request';
              const isPostDeleted = notif.type === 'post_deleted';
              const isUnread      = !notif.isRead;
              const requestId     = (notif.followRequest && typeof notif.followRequest === 'object'
                ? notif.followRequest._id : notif.followRequest) || notif.metadata?.requestId;
              const senderId      = notif.sender?._id || notif.sender?.id;
              const procKey       = requestId || senderId || id;
              const senderName    = notif.sender
                ? (notif.sender.organizationName || notif.sender.name || notif.sender.username || t('notifications.someone'))
                : isPostDeleted ? t('notifications.admin') : t('notifications.someone');
              const avatarSrc     = notif.sender?.profileImage || notif.sender?.avatar || '';
              const postContent   = (notif.post && typeof notif.post === 'object') ? notif.post.content : null;

              // Extract admin deletion reason from message or metadata
              const deletionReason = isPostDeleted
                ? (notif.metadata?.reason || null)
                : null;
              const deletedPostPreview = isPostDeleted
                ? (notif.metadata?.postContent || postContent || null)
                : null;

              return (
                <div
                  key={id}
                  className={`notif-item ${isUnread ? 'unread' : 'read'} ${isFollowReq ? 'is-follow-req' : ''} ${isPostDeleted ? 'is-post-deleted' : ''}`}
                  onClick={() => !isFollowReq && handleItemClick(notif)}
                >
                  {isUnread && <div className="notif-dot" />}
                  <div className="notif-follow-row">
                    {/* For post_deleted, show a shield/admin icon instead of avatar */}
                    <div className="notif-avatar-wrap">
                      {isPostDeleted ? (
                        <div className="notif-admin-icon">
                          <ShieldAlert size={20} color="#fff" />
                        </div>
                      ) : (
                        <Avatar src={avatarSrc} name={senderName} onClick={(e) => handleAvatarClick(e, notif)} />
                      )}
                      <div className={`notif-type-badge ${getTypeClass(notif.type)}`}>
                        {getTypeIcon(notif.type)}
                      </div>
                    </div>

                    <div className="notif-body">
                      <div className={`notif-sender-name ${isPostDeleted ? 'admin-label' : ''}`}>
                        {isPostDeleted ? t('notifications.admin') : senderName}
                      </div>
                      {(() => {
                        // Every notification type — including ones carrying a raw,
                        // backend-supplied `message` (system/admin notices, pasted
                        // links, etc.) — renders through this SAME clamped
                        // .notif-action-text treatment. Previously a raw
                        // notif.message was dumped in unclamped, so a long pasted
                        // post body with an inline URL and mid-sentence breaks
                        // could blow the card open into a wall of unstyled text.
                        if (!notif.message) {
                          return <div className="notif-action-text">{getActionText(notif.type)}</div>;
                        }
                        const { text, link } = splitTextAndLink(notif.message);
                        return (
                          <>
                            <div className="notif-action-text">{truncateText(text || notif.message, 140)}</div>
                            {link && (
                              <a
                                className="notif-link-chip"
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink size={11} />
                                {linkLabel(link, t('notifications.viewLink', 'View link'))}
                              </a>
                            )}
                          </>
                        );
                      })()}

                      {/* Show deleted post preview */}
                      {isPostDeleted && deletedPostPreview && (
                        <div className="notif-post-preview">
                          "{deletedPostPreview.substring(0, 65)}{deletedPostPreview.length > 65 ? '...' : ''}"
                        </div>
                      )}

                      {/* Show deletion reason if provided */}
                      {isPostDeleted && deletionReason && (
                        <div className="notif-deleted-reason">
                          {t('notifications.reason')}: {deletionReason}
                        </div>
                      )}

                      {/* Comment preview for comment notifications */}
                      {(notif.type === 'comment' || notif.type === 'post_comment') && postContent && (
                        <div className="notif-comment-preview">"{postContent.substring(0, 65)}"</div>
                      )}

                      <div className="notif-time"><Clock size={10} />{formatTime(notif.createdAt || notif.created_at)}</div>
                    </div>

                    {/* Delete button — always show except for follow requests */}
                    {!isFollowReq && (
                      <button className="notif-del-btn" onClick={(e) => deleteNotification(id, e)} title={t('notifications.dismiss')}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>

                  {/* Follow request action buttons */}
                  {isFollowReq && (
                    <div className="notif-follow-btns">
                      <button
                        className="btn-accept"
                        onClick={(e) => handleFollowAction(notif, 'accept', e)}
                        disabled={!!processing[procKey]}
                      >
                        <UserCheck size={12} />{processing[procKey] ? t('notifications.processing') : t('notifications.accept')}
                      </button>
                      <button
                        className="btn-decline"
                        onClick={(e) => handleFollowAction(notif, 'reject', e)}
                        disabled={!!processing[procKey]}
                      >
                        <X size={12} />{t('notifications.decline')}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="notif-footer">
            <div className="notif-stats">
              <span>{notifications.length} {t('notifications.total')}</span>
              {unreadCount > 0 && (
                <span className="notif-unread-stat">
                  <AlertCircle size={10} />{unreadCount} {t('notifications.unread')}
                </span>
              )}
            </div>
            <button className="notif-refresh-btn" onClick={loadNotifications} disabled={loading}>
              <RefreshCw size={11} className={loading ? 'notif-spinning' : ''} />{t('notifications.refresh')}
            </button>
          </div>

        </div>
      </div>
    </>
  );
};

export default NotificationModal;
